import React from "react";
import { Download, Loader2, Trash2, Upload } from "lucide-react";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { getCategoriesAction } from "action/category-action";
import { categories as presetCategories } from "helpers/constants";
import {
  productBulkImportConfirmAction,
  productBulkImportExtractAction,
  productBulkImportStatusAction,
} from "action/product-action";
import { toast } from "sonner";

type DraftRow = {
  key: number;
  name: string;
  price: string;
  category: string;
  description: string;
  image_url: string;
  available_after_days: string | number;
  featured: boolean;
  today_special: boolean;
  available: boolean;
  serverErrors: string[];
};

type Step = "upload" | "processing" | "review";

// Photos (.png,.jpg,.jpeg,.webp) can be added here once the backend LLM_SUPPORTS_VISION is on.
const ACCEPTED = ".csv,.xlsx,.pdf,.txt";
const POLL_INTERVAL_MS = 2000;
const MAX_POLLS = 150;

const template = `name,price,category,description,image_url,featured,today_special,available,available_after_days\nClassic T-Shirt,5000,Clothing,Soft cotton t-shirt,,false,false,true,\n`;

// Mirrors the server's required checks so the user sees problems while editing; the server stays the authority.
function problemsFor(row: DraftRow): string[] {
  if (row.serverErrors.length > 0) return row.serverErrors;
  const problems: string[] = [];
  if (!row.name.trim()) problems.push("name is required");
  const price = row.price.trim().replace(/,/g, "");
  if (!price) problems.push("price is required");
  else if (isNaN(Number(price)) || Number(price) < 0) problems.push("price must be a valid number");
  return problems;
}

export default function BulkProductImport({ onImported }: { onImported: () => void }) {
  const [open, setOpen] = React.useState(false);
  const [step, setStep] = React.useState<Step>("upload");
  const [file, setFile] = React.useState<File | null>(null);
  const [text, setText] = React.useState("");
  const [rows, setRows] = React.useState<DraftRow[]>([]);
  const [warnings, setWarnings] = React.useState<string[]>([]);
  const [saving, setSaving] = React.useState(false);
  const [knownCategories, setKnownCategories] = React.useState<string[]>(presetCategories);
  const runId = React.useRef(0);

  // Presets plus the business's own categories: what the AI should prefer and the review list offers.
  React.useEffect(() => {
    if (!open) return;
    getCategoriesAction().then((response) => {
      if (response.status === 200 && Array.isArray(response.body)) {
        const custom = response.body.map((category) => category.name);
        setKnownCategories(Array.from(new Set([...presetCategories, ...custom])));
      }
    });
  }, [open]);

  const reset = () => {
    runId.current += 1; // abandons any in-flight polling
    setStep("upload");
    setFile(null);
    setText("");
    setRows([]);
    setWarnings([]);
    setSaving(false);
  };

  const downloadTemplate = () => {
    const url = URL.createObjectURL(new Blob([template], { type: "text/csv;charset=utf-8" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = "storbolt-products-template.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  const extract = async () => {
    const run = ++runId.current;
    setStep("processing");
    const started = await productBulkImportExtractAction(file, text, knownCategories);
    if (run !== runId.current) return;
    if (started.status !== 202) {
      toast.error(started.message || "Unable to read your products.");
      setStep("upload");
      return;
    }

    for (let attempt = 0; attempt < MAX_POLLS; attempt++) {
      await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
      if (run !== runId.current) return;
      const result = await productBulkImportStatusAction(started.body.taskId);
      if (run !== runId.current) return;
      if (result.status !== 200) {
        toast.error(result.message || "Unable to read your products.");
        setStep("upload");
        return;
      }
      if (result.body.status === "failed") {
        toast.error(result.body.message);
        setStep("upload");
        return;
      }
      if (result.body.status === "done") {
        setRows(
          result.body.rows.map((row: any, index: number) => ({
            ...row,
            key: index,
            serverErrors: [],
          })),
        );
        setWarnings(result.body.warnings || []);
        setStep("review");
        return;
      }
    }
    toast.error("This is taking too long. Try a smaller file.");
    setStep("upload");
  };

  const updateRow = (key: number, changes: Partial<DraftRow>) =>
    setRows((current) =>
      current.map((row) => (row.key === key ? { ...row, ...changes, serverErrors: [] } : row)),
    );

  const removeRow = (key: number) => setRows((current) => current.filter((row) => row.key !== key));

  const invalidCount = rows.filter((row) => problemsFor(row).length > 0).length;

  const confirm = async () => {
    setSaving(true);
    const payload = rows.map(({ key, serverErrors, ...row }) => row);
    const known = new Set(knownCategories.map((name) => name.toLowerCase()));
    const newCategories = Array.from(
      new Set(
        rows
          .map((row) => row.category.trim())
          .filter((name) => name && !known.has(name.toLowerCase())),
      ),
    );
    const response = await productBulkImportConfirmAction(payload, newCategories);
    setSaving(false);
    if (response.status !== 201) {
      const errors: { row: number; errors: string[] }[] = response.body?.errors || [];
      if (errors.length > 0) {
        setRows((current) =>
          current.map((row, index) => ({
            ...row,
            serverErrors: errors.find((error) => error.row === index + 1)?.errors || [],
          })),
        );
      }
      toast.error(response.message || "Unable to import products.");
      return;
    }
    toast.success(response.message);
    setOpen(false);
    reset();
    onImported();
  };

  const canExtract = step === "upload" && (file !== null || text.trim().length > 0);

  return (
    <Dialog open={open} onOpenChange={(next) => { setOpen(next); if (!next) reset(); }}>
      <Button variant="outline" className="h-8" onClick={() => setOpen(true)}>
        <Upload className="mr-2 h-4 w-4" /> Bulk upload
      </Button>
      <DialogContent
        className={step === "review" ? "md:max-w-[1180px]" : "md:max-w-[560px]"}
      >
        <DialogHeader>
          <DialogTitle>
            {step === "review" ? "Review your products" : "Bulk upload products"}
          </DialogTitle>
          <DialogDescription>
            {step === "review"
              ? "Check and fix anything before importing. You can add product images afterwards."
              : "Upload a spreadsheet or PDF of your product list, or paste it in. We'll pull out the names and prices for you to review."}
          </DialogDescription>
        </DialogHeader>

        {step === "upload" && (
          <div className="space-y-4">
            <Input
              type="file"
              accept={ACCEPTED}
              onChange={(event) => setFile(event.target.files?.[0] || null)}
            />
            <p className="text-xs text-default-500">
              CSV, Excel (.xlsx), text-based PDF or text file, up to 8 MB.
            </p>
            {!file && (
              <>
                <p className="text-center text-sm text-default-500">or paste your list</p>
                <textarea
                  className="min-h-[120px] w-full rounded-md border border-default-300 bg-transparent p-3 text-sm"
                  placeholder={"Jollof rice - 2500\nFried rice - 2800\nChicken & chips - 4500"}
                  value={text}
                  onChange={(event) => setText(event.target.value)}
                />
              </>
            )}
            <Button type="button" variant="outline" size="sm" onClick={downloadTemplate}>
              <Download className="mr-2 h-4 w-4" /> Download CSV template
            </Button>
          </div>
        )}

        {step === "processing" && (
          <div className="flex flex-col items-center gap-3 py-10 text-sm text-default-600">
            <Loader2 className="h-6 w-6 animate-spin" />
            Reading your products. This can take up to a minute...
          </div>
        )}

        {step === "review" && (
          <div className="space-y-3">
            {warnings.map((warning) => (
              <p key={warning} className="text-sm text-amber-600">{warning}</p>
            ))}
            <p className="text-sm text-default-700">
              {rows.length} product{rows.length === 1 ? "" : "s"} found
              {invalidCount > 0 && (
                <span className="text-destructive"> · {invalidCount} need{invalidCount === 1 ? "s" : ""} attention</span>
              )}
            </p>
            <datalist id="bulk-import-categories">
              {knownCategories.map((name) => <option key={name} value={name} />)}
            </datalist>
            <div className="max-h-[60vh] overflow-auto rounded-md border">
              <table className="w-full table-fixed text-sm">
                <colgroup>
                  <col className="w-[30%]" />
                  <col className="w-[130px]" />
                  <col className="w-[22%]" />
                  <col />
                  <col className="w-12" />
                </colgroup>
                <thead className="sticky top-0 z-10 bg-background text-left text-xs text-default-500">
                  <tr>
                    <th className="p-2">Name</th>
                    <th className="p-2">Price (₦)</th>
                    <th className="p-2">Category</th>
                    <th className="p-2">Description</th>
                    <th className="p-2" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const problems = problemsFor(row);
                    return (
                      <React.Fragment key={row.key}>
                        <tr className="border-t align-top">
                          <td className="p-1"><Input value={row.name} onChange={(e) => updateRow(row.key, { name: e.target.value })} /></td>
                          <td className="p-1"><Input value={row.price} inputMode="decimal" onChange={(e) => updateRow(row.key, { price: e.target.value })} /></td>
                          <td className="p-1">
                            <Input
                              list="bulk-import-categories"
                              value={row.category}
                              onChange={(e) => updateRow(row.key, { category: e.target.value })}
                            />
                            {row.category.trim() &&
                              !knownCategories.some((name) => name.toLowerCase() === row.category.trim().toLowerCase()) && (
                                <span className="ml-1 text-[10px] font-medium text-primary">New category</span>
                              )}
                          </td>
                          <td className="p-1"><Input value={row.description} onChange={(e) => updateRow(row.key, { description: e.target.value })} /></td>
                          <td className="p-1">
                            <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => removeRow(row.key)} aria-label="Remove product">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </td>
                        </tr>
                        {problems.length > 0 && (
                          <tr>
                            <td colSpan={5} className="px-3 pb-2 text-xs text-destructive">{problems.join(", ")}</td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <DialogFooter>
          {step === "review" ? (
            <>
              <Button type="button" variant="outline" onClick={reset}>Start over</Button>
              <Button type="button" disabled={saving || rows.length === 0 || invalidCount > 0} onClick={confirm}>
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Import {rows.length} product{rows.length === 1 ? "" : "s"}
              </Button>
            </>
          ) : (
            <>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="button" disabled={!canExtract} onClick={extract}>
                {step === "processing" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Review products
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
